/**
 * `ParamProvider` and the `useScreenParams` / `useInstanceParams` hooks for
 * screen-scoped URL params.
 *
 * URLs encode params for two scopes — instance-level (no suffix) and
 * screen-level (numeric suffix). `instance.tsx` parses both at the route level
 * and passes them through `ParamProvider`; this file is just the React glue
 * that exposes them to leaf components. `useScreenParams` first consults the
 * `Datastore` for test-mode params so demo storysets can override URL state
 * without touching the real router.
 */

import React, { useMemo } from 'react';

import { useDatastore } from './datastore';

type ParamContextType = {
    instanceParams: Record<string, string>;
    screenParams: Record<string, string>;
};

const ParamContext = React.createContext<ParamContextType | null>(null);

export function ParamProvider({ instanceParams, screenParams, children }: React.PropsWithChildren<ParamContextType>) {
    const contextValue = useMemo(() => ({ instanceParams, screenParams }), [instanceParams, screenParams]);
    return <ParamContext.Provider value={contextValue}>{children}</ParamContext.Provider>;
}

export function useScreenParams(): Record<string, string> {
    const datastore = useDatastore();
    const paramContext = React.useContext(ParamContext);

    return datastore?.getTestScreenParams() ?? paramContext?.screenParams ?? {};
}

export function useInstanceParams(): Record<string, string> {
    return React.useContext(ParamContext)?.instanceParams ?? {};
}
